import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, retry, timeout } from 'rxjs/operators';
import { defaultTranslations } from '../translations/default-translations';

export type Language = 'en' | 'de' | 'nl' | 'hu';
export type LanguageSelectionMethod =
  | 'localStorage'
  | 'ipGeolocation'
  | 'browserLanguage'
  | 'default';

export interface IpGeolocationData {
  ip: string;
  country_name: string;
  country_code: string;
  city: string;
  region: string;
  detected_language?: string;
}

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  private currentLanguageSubject = new BehaviorSubject<Language>('en');
  public currentLanguage$ = this.currentLanguageSubject.asObservable();

  private languageSelectionMethodSubject =
    new BehaviorSubject<LanguageSelectionMethod>('default');
  public languageSelectionMethod$ =
    this.languageSelectionMethodSubject.asObservable();

  private ipGeolocationDataSubject =
    new BehaviorSubject<IpGeolocationData | null>(null);
  public ipGeolocationData$ = this.ipGeolocationDataSubject.asObservable();

  // Use a Map for translations to reduce memory overhead
  private translations = new Map<string, string>();
  private loadedCategories = new Set<string>();
  private maxRetries = 1; // Reduce retries to minimize memory usage

  constructor(private http: HttpClient) {
    this.initializeLanguage();
    this.initializeDefaultTranslations();
  }

  private initializeDefaultTranslations(): void {
    Object.entries(defaultTranslations).forEach(([lang, translations]) => {
      this.mergeTranslations(translations, lang);
    });
  }

  private initializeLanguage(): void {
    if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
      const savedLanguage = this.getSavedLanguage();
      if (savedLanguage) {
        this.setLanguage(savedLanguage);
        this.languageSelectionMethodSubject.next('localStorage');
        return;
      }
      this.detectBrowserLanguage();
    } else {
      this.setLanguage('en');
    }
  }

  private detectBrowserLanguage(): void {
    try {
      const browserLang = navigator.language.split('-')[0];
      const supportedLanguages: Language[] = ['en', 'de', 'nl', 'hu'];

      if (browserLang && supportedLanguages.includes(browserLang as Language)) {
        this.setLanguage(browserLang as Language);
        this.languageSelectionMethodSubject.next('browserLanguage');
        return;
      }
    } catch (error) {
      console.warn('Error detecting browser language:', error);
    }

    this.setLanguage('en');
    this.languageSelectionMethodSubject.next('default');
  }

  private getSavedLanguage(): Language | null {
    if (typeof localStorage !== 'undefined') {
      try {
        const saved = localStorage.getItem('preferredLanguage') as Language;
        if (saved && ['en', 'de', 'nl', 'hu'].includes(saved)) {
          return saved;
        }
      } catch (error) {
        console.warn('Error accessing localStorage:', error);
      }
    }
    return null;
  }

  public setLanguage(language: Language): void {
    this.currentLanguageSubject.next(language);

    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
    }

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('preferredLanguage', language);
    }

    this.loadTranslationsFile(language);
    this.loadedCategories.forEach((category) => {
      this.loadCategoryTranslations(language, category).subscribe();
    });
  }

  public getCurrentLanguage(): Language {
    return this.currentLanguageSubject.value;
  }

  public getLanguageSelectionMethod(): LanguageSelectionMethod {
    return this.languageSelectionMethodSubject.value;
  }

  public getIpGeolocationData(): IpGeolocationData | null {
    return this.ipGeolocationDataSubject.value;
  }

  // Make mergeTranslations protected so it can be accessed by child classes
  protected mergeTranslations(
    translations: Record<string, any>,
    prefix = ''
  ): void {
    Object.entries(translations).forEach(([key, value]) => {
      if (typeof value === 'string') {
        this.translations.set(`${prefix}${key}`, value);
      } else if (typeof value === 'object' && value !== null) {
        this.mergeTranslations(value, `${prefix}${key}.`);
      }
    });
  }

  public translate(key: string): string {
    const translation = this.translations.get(key);
    if (translation) {
      return translation;
    }

    // Try to get from default translations
    const defaultTranslation = this.getFromDefaultTranslations(
      this.getCurrentLanguage(),
      key
    );
    return defaultTranslation || key;
  }

  private getFromDefaultTranslations(
    language: Language,
    key: string
  ): string | null {
    if (!defaultTranslations[language]) {
      return null;
    }

    const keys = key.split('.');
    let result: any = defaultTranslations[language];

    for (const k of keys) {
      if (result && result[k] !== undefined) {
        result = result[k];
      } else {
        return null;
      }
    }

    return typeof result === 'string' ? result : null;
  }

  public loadTranslationsFile(language: Language): void {
    this.http
      .get<Record<string, any>>(`assets/i18n/${language}.json`)
      .pipe(
        timeout(3000),
        retry(this.maxRetries),
        catchError(() => of({}))
      )
      .subscribe((translations) => {
        this.mergeTranslations(translations, '');
      });
  }

  public loadCategoryTranslations(
    language: Language,
    category: string
  ): Observable<void> {
    this.loadedCategories.add(category);

    return this.http
      .get<Record<string, any>>(
        `assets/i18n/categories/${category}.${language}.json`
      )
      .pipe(
        timeout(3000),
        retry(this.maxRetries),
        map((translations) => {
          this.mergeTranslations(translations, '');
        }),
        catchError(() => {
          // Try English fallback if not already using English
          if (language !== 'en') {
            return this.http
              .get<Record<string, any>>(
                `assets/i18n/categories/${category}.en.json`
              )
              .pipe(
                timeout(3000),
                map((translations) => {
                  this.mergeTranslations(translations, '');
                }),
                catchError(() => of(undefined))
              );
          }
          return of(undefined);
        })
      );
  }

  // Add method to get translations for child classes
  protected getTranslations(): Map<string, string> {
    return this.translations;
  }
}
